import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Chip,
  InputAdornment,
  TablePagination,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Print as PrintIcon,
  FactCheck as ReportIcon
} from '@mui/icons-material';
import api from '../services/api';

const SupervisionReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] = useState<any>(null);

  const handleOpenDetails = (supervisor: any) => {
    setSelectedSupervisor(supervisor);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedSupervisor(null);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/evaluations/supervision-report');
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch supervision report data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredData = data.filter((item: any) => {
    // 1. Text Search Filter
    const term = searchTerm.toLowerCase();
    const matchesText = !term || (
      item.name?.toLowerCase().includes(term) ||
      item.student_names_prof?.toLowerCase().includes(term) ||
      item.student_names_spec?.toLowerCase().includes(term)
    );

    if (!matchesText) return false;

    // 2. Status Filter
    switch (statusFilter) {
      case 'PENDING_PROF_NONE':
        return item.prof_evaluated === 0;
      case 'DONE_PROF':
        return item.prof_evaluated > 0;
      case 'PENDING_SPEC_NONE':
        return item.spec_evaluated === 0;
      case 'DONE_SPEC':
        return item.spec_evaluated > 0;
      case 'ALL':
      default:
        return true;
    }
  });

  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getStatusChipForRound = (assigned: number, evaluated: number, isProfRound = false) => {
    let label = 'ยังไม่เคยนิเทศ';
    let color: 'success' | 'warning' | 'info' | 'default' = 'warning';
    let variant: 'filled' | 'outlined' = 'outlined';

    if (isProfRound) {
      if (evaluated > 0) {
        label = `นิเทศแล้ว (${evaluated} คน)`;
        color = 'success';
        variant = 'filled';
      } else {
        label = 'ยังไม่เคยนิเทศ';
        color = 'warning';
        variant = 'outlined';
      }
    } else {
      // Specialist Round
      if (assigned === 0) {
        label = 'ยังไม่ได้มอบหมาย';
        color = 'default';
        variant = 'outlined';
      } else if (evaluated > 0) {
        if (evaluated === assigned) {
          label = `นิเทศครบแล้ว (${evaluated} คน)`;
          color = 'success';
          variant = 'filled';
        } else {
          label = `นิเทศแล้ว (${evaluated}/${assigned} คน)`;
          color = 'info';
          variant = 'filled';
        }
      } else {
        label = `ยังไม่เคยนิเทศ (${assigned} คน)`;
        color = 'warning';
        variant = 'outlined';
      }
    }

    return (
      <Chip 
        label={label} 
        size="small" 
        color={color} 
        variant={variant}
        sx={{ 
          fontWeight: 'bold', 
          fontSize: '0.72rem',
          minWidth: 145,
          height: 24
        }} 
      />
    );
  };

  return (
    <Box className="report-page-container" sx={{ width: '100%' }}>
      {/* Header section (Hidden on print) */}
      <Box className="no-print" sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: 'wrap',
        gap: 2,
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ReportIcon sx={{ mr: 1, fontSize: 30, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 800 }}>รายงานติดตามการนิเทศ (Supervision Follow-up Report)</Typography>
        </Box>
        <Button 
          variant="outlined" 
          color="primary" 
          startIcon={<PrintIcon />} 
          onClick={handlePrint}
          sx={{ borderRadius: 2, fontWeight: 'bold' }}
        >
          พิมพ์รายงาน
        </Button>
      </Box>

      {/* Print Only Header (Visible only on print) */}
      <Box className="print-only" sx={{ display: 'none', mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
          รายงานติดตามสถานะการนิเทศ (อาจารย์นิเทศก์)
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">
          ข้อมูล ณ วันที่ {new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Box>

      {/* Search and Filters card (Hidden on print) */}
      <Paper className="no-print" sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="ค้นหาตามชื่ออาจารย์ หรือนักศึกษาในดูแล..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="กรองตามสถานะการนิเทศ"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="ALL">แสดงทั้งหมด</MenuItem>
              <MenuItem value="DONE_PROF">รอบวิชาชีพครู: นิเทศแล้ว</MenuItem>
              <MenuItem value="PENDING_PROF_NONE">รอบวิชาชีพครู: ยังไม่เคยนิเทศ</MenuItem>
              <MenuItem value="DONE_SPEC">รอบวิชาเอก: นิเทศแล้ว</MenuItem>
              <MenuItem value="PENDING_SPEC_NONE">รอบวิชาเอก: ยังไม่เคยนิเทศ</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
            {(searchTerm || statusFilter !== 'ALL') && (
              <Button 
                size="small" 
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setPage(0);
                }}
                sx={{ mr: 1, fontWeight: 'bold' }}
              >
                ล้างตัวกรอง
              </Button>
            )}
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              พบ {filteredData.length} รายการ
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table Paper */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2 }} color="textSecondary">กำลังดึงข้อมูลรายงาน...</Typography>
          </Box>
        ) : (
          <>
            <TableContainer id="printable-table-container">
              <Table size="small" aria-label="supervision report table" sx={{ minWidth: 950 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell align="center" className="col-seq" sx={{ fontWeight: 'bold', width: '5%', bgcolor: '#f1f5f9' }}>ลำดับ</TableCell>
                    <TableCell className="col-supervisor" sx={{ fontWeight: 'bold', width: '25%' }}>อาจารย์นิเทศก์</TableCell>
                    <TableCell align="center" className="col-prof" sx={{ fontWeight: 'bold', width: '27%', bgcolor: 'rgba(25, 118, 210, 0.02)' }}>
                      รอบวิชาชีพครู
                    </TableCell>
                    <TableCell align="center" className="col-spec" sx={{ fontWeight: 'bold', width: '28%', bgcolor: 'rgba(156, 39, 176, 0.02)' }}>
                      รอบวิชาเอก
                    </TableCell>
                    <TableCell align="center" className="no-print" sx={{ fontWeight: 'bold', width: '15%' }}>
                      รายละเอียด
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row: any, idx: number) => {
                    const globalIdx = page * rowsPerPage + idx + 1;
                    return (
                      <TableRow key={row.user_id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell align="center" className="col-seq" sx={{ fontWeight: 500, color: 'text.secondary', bgcolor: '#fbfcfd' }}>
                          {globalIdx}
                        </TableCell>
                        <TableCell className="col-supervisor" sx={{ fontWeight: 600 }}>
                          {row.name}
                        </TableCell>
                        
                        {/* Professional Round Column */}
                        <TableCell align="center" className="col-prof" sx={{ bgcolor: 'rgba(25, 118, 210, 0.01)' }}>
                          {getStatusChipForRound(row.prof_assigned, row.prof_evaluated, true)}
                        </TableCell>
                        
                        {/* Specialist Round Column */}
                        <TableCell align="center" className="col-spec" sx={{ bgcolor: 'rgba(156, 39, 176, 0.01)' }}>
                          {getStatusChipForRound(row.spec_assigned, row.spec_evaluated, false)}
                        </TableCell>

                        {/* Details Action Column */}
                        <TableCell align="center" className="no-print">
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => handleOpenDetails(row)}
                            sx={{ borderRadius: 2, fontWeight: 'bold' }}
                          >
                            รายละเอียด
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Typography color="textSecondary">ไม่พบข้อมูลรายงาน</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              className="no-print"
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="รายการต่อหน้า:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          รายละเอียดการนิเทศ - {selectedSupervisor?.name}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={3}>
            {/* Professional Round Details */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2.5, height: '100%', borderRadius: 3, bgcolor: 'rgba(25, 118, 210, 0.01)', border: '1px solid rgba(25, 118, 210, 0.08)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
                  รอบวิชาชีพครู
                </Typography>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    สถานะการนิเทศ:
                  </Typography>
                  {selectedSupervisor && getStatusChipForRound(selectedSupervisor.prof_assigned, selectedSupervisor.prof_evaluated, true)}
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                  รายชื่อนักศึกษาที่ออกนิเทศ ({selectedSupervisor?.prof_evaluated || 0} คน):
                </Typography>
                {selectedSupervisor?.student_names_prof ? (
                  <List dense sx={{ pl: 1 }}>
                    {selectedSupervisor.student_names_prof.split(', ').map((name: string, i: number) => (
                      <ListItem key={i} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText 
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              {i + 1}. {name}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', pl: 1, py: 1 }}>
                    ไม่มีรายชื่อนักศึกษาที่นิเทศในรอบนี้
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Specialist Round Details */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2.5, height: '100%', borderRadius: 3, bgcolor: 'rgba(156, 39, 176, 0.01)', border: '1px solid rgba(156, 39, 176, 0.08)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 2 }}>
                  รอบวิชาเอก
                </Typography>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    สถานะการนิเทศ:
                  </Typography>
                  {selectedSupervisor && getStatusChipForRound(selectedSupervisor.spec_assigned, selectedSupervisor.spec_evaluated)}
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <b>จำนวนที่ได้รับมอบหมาย:</b> {selectedSupervisor?.spec_assigned || 0} คน
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <b>ประเมินแล้ว:</b> {selectedSupervisor?.spec_evaluated || 0} คน
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                  รายชื่อนักศึกษาในความดูแล ({selectedSupervisor?.spec_assigned || 0} คน):
                </Typography>
                {selectedSupervisor?.student_names_spec ? (
                  <List dense sx={{ pl: 1 }}>
                    {selectedSupervisor.student_names_spec.split(', ').map((name: string, i: number) => (
                      <ListItem key={i} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText 
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              {i + 1}. {name}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', pl: 1, py: 1 }}>
                    ไม่มีรายชื่อนักศึกษาที่นิเทศในรอบนี้
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button onClick={handleCloseDetails} variant="contained" color="primary" sx={{ borderRadius: 2, fontWeight: 'bold', px: 3 }}>
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Overrides CSS */}
      <style>
        {`
          @media print {
            @page {
              size: A4 landscape;
              margin: 6mm 8mm;
            }
            body {
              background: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            /* Hide Sidebar, Header, Toolbar, dialogs, pagination and other non-print elements */
            header,
            nav,
            .MuiAppBar-root,
            .MuiDrawer-root,
            .MuiToolbar-root,
            .MuiDialog-root,
            .MuiTablePagination-root,
            .no-print {
              display: none !important;
              width: 0 !important;
              height: 0 !important;
              overflow: hidden !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            /* Reset parent containers layout to full width */
            #root,
            main,
            .MuiContainer-root,
            .report-page-container {
              padding: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              min-width: 100% !important;
              display: block !important;
              position: static !important;
              box-shadow: none !important;
              border: none !important;
            }
            /* Override the desktop flex drawer layout in Dashboard.tsx to take full page width */
            #root > div,
            #root > .MuiBox-root {
              display: block !important;
              padding: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
            }
            .print-only {
              display: block !important;
            }
            
            /* Remove MUI Paper shadows, borders, and margins on print */
            .MuiPaper-root {
              box-shadow: none !important;
              border: none !important;
              background: transparent !important;
              overflow: visible !important;
              margin: 0 !important;
              padding: 0 !important;
            }

            /* Print Table Formatting */
            #printable-table-container {
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              box-shadow: none !important;
              border: none !important;
              overflow: visible !important;
            }
            #printable-table-container table {
              width: 100% !important;
              border-collapse: collapse !important;
              table-layout: fixed !important;
            }
            #printable-table-container th, 
            #printable-table-container td {
              border: 1px solid rgba(0,0,0,0.2) !important;
              padding: 6px 4px !important;
              font-size: 0.72rem !important;
              color: black !important;
              word-break: break-word !important;
            }
            #printable-table-container th {
              background-color: #f1f5f9 !important;
              font-weight: bold !important;
            }
            
            /* Specific Column Widths on Print (Must equal 100%) */
            .col-seq {
              width: 6% !important;
            }
            .col-supervisor {
              width: 34% !important;
            }
            .col-prof {
              width: 30% !important;
            }
            .col-spec {
              width: 30% !important;
            }

            /* Status Chip Styles for Print */
            .MuiChip-root {
              border: 1px solid rgba(0,0,0,0.15) !important;
              border-radius: 4px !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .MuiChip-colorSuccess {
              background-color: #e8f5e9 !important;
              color: #1b5e20 !important;
            }
            .MuiChip-colorWarning {
              background-color: #fff3e0 !important;
              color: #e65100 !important;
            }
            .MuiChip-colorInfo {
              background-color: #e3f2fd !important;
              color: #0d47a1 !important;
            }
            .MuiChip-colorDefault {
              background-color: #f1f5f9 !important;
              color: #475569 !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default SupervisionReport;
